import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "../i18n";
import { useLenis } from "./SmoothScroll";
import { scrollToId } from "./Header";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=2000&q=80";

export const Hero = () => {
  const { t } = useLang();
  const lenisRef = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="accueil" ref={ref} data-testid="hero-section" className="grain relative flex min-h-screen flex-col overflow-hidden bg-oris-deep">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={HERO_IMG}
          alt="Port de commerce international et navires cargo"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-oris-deep/60" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_80%,transparent_30%,rgba(15,41,24,0.55)_100%)]" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-5 pb-16 pt-40 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          data-testid="hero-overline"
          className="mb-8 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-oris-gold"
        >
          <span className="gold-rule" aria-hidden="true" />
          {t.hero.overline}
        </motion.p>

        <h1
          data-testid="hero-title"
          className="max-w-5xl font-serif text-[13vw] font-medium leading-[0.98] tracking-tight text-oris-cream sm:text-7xl lg:text-8xl"
        >
          {t.hero.lines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.05, delay: 0.35 + i * 0.16, ease: EASE }}
              >
                {i === 2 ? <em className="italic text-oris-gold">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl text-base leading-relaxed text-oris-cream/80 lg:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-products"
            onClick={() => scrollToId(lenisRef, "produits")}
            className="group inline-flex items-center gap-2 bg-oris-gold px-7 py-4 text-[0.8rem] font-bold uppercase tracking-[0.16em] text-oris-ink transition-colors duration-300 hover:bg-oris-cream"
          >
            {t.hero.ctaPrimary}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToId(lenisRef, "contact")}
            className="inline-flex items-center gap-2 border border-oris-cream/40 px-7 py-4 text-[0.8rem] font-bold uppercase tracking-[0.16em] text-oris-cream transition-colors duration-300 hover:border-oris-cream hover:bg-oris-cream hover:text-oris-deep"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          data-testid="hero-stats"
          className="mt-16 grid grid-cols-2 gap-y-8 border-t border-oris-cream/15 pt-8 md:grid-cols-4"
        >
          {t.hero.stats.map((s) => (
            <div key={s.label}>
              <div className="font-serif text-3xl font-medium text-oris-gold lg:text-4xl">{s.value}</div>
              <div className="mt-1 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-oris-cream/60">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-6 right-6 z-10 hidden items-center gap-2 text-oris-cream/60 lg:flex"
      >
        <span className="text-[0.65rem] font-semibold uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.div>
    </section>
  );
};
